
import { useEffect, useState } from 'react';

import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  EmptyState,
  Pill,
  PlayButton,
  SearchInput,
  SearchableSelect,
  SkeletonRows,
  type AudioPreview,
  type SearchableOption,
} from '../../components/ui';
import { LANGUAGES } from '../../lib/languages';
import type { SharedVoice } from '../../types/api';
import { useAddVoiceFromLibrary, useVoiceLibrary } from '../voices/catalogQueries';
import { VOICE_AGES, VOICE_GENDERS } from '../voices/vocab';
import styles from './TtsPage.module.css';
import { formatCount } from './format';
import { describeSharedVoice } from './voiceOptions';

const PAGE_SIZE = 12;

const LANGUAGE_OPTIONS: SearchableOption[] = LANGUAGES.map((item) => ({
  value: item.code,
  label: item.label,
}));

const GENDER_OPTIONS: SearchableOption[] = VOICE_GENDERS.map((item) => ({
  value: item.value,
  label: item.label,
}));

const AGE_OPTIONS: SearchableOption[] = VOICE_AGES.map((item) => ({
  value: item.value,
  label: item.label,
}));

interface Props {
  canAdd: boolean;
  onAdded: (voice: SharedVoice, externalVoiceId: string) => void;
  audio: AudioPreview;
  /** Вкладка открыта. Пока нет, к платному провайдеру не ходим. */
  active: boolean;
}

export function VoiceLibraryPanel({ canAdd, onAdded, audio, active }: Props) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [language, setLanguage] = useState<string | null>(null);
  const [gender, setGender] = useState<string | null>(null);
  const [age, setAge] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  // библиотека живёт у платного провайдера: пока блок закрыт, запросов нет
  const library = useVoiceLibrary(
    {
      q: debounced || undefined,
      language: language ?? undefined,
      gender: gender ?? undefined,
      age: age ?? undefined,
      page_size: PAGE_SIZE,
    },
    active,
  );
  const add = useAddVoiceFromLibrary();

  const voices = library.data?.pages.flatMap((item) => item.voices) ?? [];
  const total = library.data?.pages[0]?.total ?? 0;
  const addingId = add.isPending ? add.variables.voice_id : null;
  const filtered = Boolean(debounced || language || gender || age);

  const reset = () => {
    setQuery('');
    setLanguage(null);
    setGender(null);
    setAge(null);
  };

  return (
    <Card className={styles.libOpen}>
      <div className={styles.libHeadStatic}>
        <span className="eyebrow">библиотека elevenlabs</span>
        <span className={styles.pickerCount}>{total > 0 ? formatCount(total) : ''}</span>
      </div>

      <>
          <div className={styles.libFilters}>
            <SearchInput
              size="sm"
              value={query}
              onChange={setQuery}
              placeholder="Имя или английское слово"
              ariaLabel="Поиск по библиотеке ElevenLabs"
            />
            <div className={styles.libFilterRow}>
              <SearchableSelect
                fullWidth
                clearable
                options={LANGUAGE_OPTIONS}
                value={language}
                onChange={setLanguage}
                placeholder="Любой язык"
                clearLabel="Любой язык"
                ariaLabel="Язык голоса"
              />
              <SearchableSelect
                fullWidth
                clearable
                options={GENDER_OPTIONS}
                value={gender}
                onChange={setGender}
                placeholder="Любой пол"
                clearLabel="Любой пол"
                ariaLabel="Пол голоса"
              />
              <SearchableSelect
                fullWidth
                clearable
                options={AGE_OPTIONS}
                value={age}
                onChange={setAge}
                placeholder="Любой возраст"
                clearLabel="Любой возраст"
                ariaLabel="Возраст голоса"
              />
            </div>
            <p className={styles.libNote}>
              {canAdd
                ? 'Голос станет доступен всей команде.'
                : 'Наблюдатель может слушать, но не добавлять.'}
            </p>
          </div>

          <div className={styles.libList}>
            {add.error && (
              <div className={styles.pad}>
                <ErrorBanner error={add.error} />
              </div>
            )}

            {library.isPending && !library.error && (
              <div className={styles.pad}>
                <SkeletonRows rows={4} />
              </div>
            )}

            {library.error && (
              <div className={styles.pad}>
                <ErrorBanner error={library.error} onRetry={() => void library.refetch()} />
              </div>
            )}

            {!library.isPending && !library.error && voices.length === 0 && (
              <EmptyState
                compact
                title="Ничего не нашлось"
                description="Поиск идёт по названию и описанию, а их пишут латиницей. Русские слова тут почти не встречаются, отбирайте фильтрами."
                action={
                  filtered ? (
                    <Button size="sm" onClick={reset}>
                      Сбросить
                    </Button>
                  ) : undefined
                }
              />
            )}

            {voices.map((voice) => {
              const key = `shared:${voice.public_owner_id}:${voice.voice_id}`;
              const playing = audio.playingKey === key;
              return (
                <div key={key} className={styles.libRow}>
                  <PlayButton
                    size="sm"
                    playing={playing}
                    disabled={!voice.preview_url}
                    onClick={() =>
                      voice.preview_url ? audio.toggle(key, voice.preview_url) : undefined
                    }
                    title={
                      voice.preview_url
                        ? playing
                          ? 'Остановить'
                          : 'Прослушать образец'
                        : 'Провайдер не приложил образец'
                    }
                    ariaLabel={
                      playing ? 'Остановить образец' : `Прослушать образец: ${voice.name}`
                    }
                  />

                  <span className={styles.libRowBody}>
                    <span className={styles.voiceNameText}>{voice.name}</span>
                    <span className={styles.voiceMeta} title={voice.description || undefined}>
                      {describeSharedVoice(voice, language) || voice.category}
                    </span>
                  </span>

                  {voice.account_voice_id ? (
                    <Button
                      size="xs"
                      onClick={() => onAdded(voice, voice.account_voice_id as string)}
                      title="Голос уже в аккаунте — просто выбрать"
                    >
                      Выбрать
                    </Button>
                  ) : voice.already_added ? (
                    <Pill tone="ok">в аккаунте</Pill>
                  ) : canAdd ? (
                    <Button
                      size="xs"
                      onClick={() =>
                        add.mutate(
                          {
                            public_owner_id: voice.public_owner_id,
                            voice_id: voice.voice_id,
                            name: voice.name,
                          },
                          {
                            onSuccess: (result) => onAdded(voice, result.external_voice_id),
                          },
                        )
                      }
                      disabled={add.isPending}
                      title="Голос станет доступен всей команде"
                    >
                      {addingId === voice.voice_id ? 'Добавляю…' : 'Добавить и выбрать'}
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {voices.length > 0 && (
            <div className={styles.libPager}>
              <span className={styles.pickerCount}>
                {formatCount(voices.length)}
                {total > 0 ? ` из ${formatCount(total)}` : ''}
              </span>
              {library.hasNextPage && (
                <Button
                  size="xs"
                  onClick={() => void library.fetchNextPage()}
                  disabled={library.isFetchingNextPage}
                >
                  {library.isFetchingNextPage ? 'Гружу…' : 'Показать ещё'}
                </Button>
              )}
            </div>
          )}
        </>
    </Card>
  );
}
